import React, { useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route, NavLink, useLocation } from 'react-router-dom';
import Overview from './pages/Overview';
import RealTimeMeetings from './pages/RealTimeMeetings';
import HistoricalMeetings from './pages/HistoricalMeetings';
import ParticipantsList from './pages/ParticipantsList';
import SipDevices from './pages/SipDevices';
import CloudMeetingConnector from './pages/CloudMeetingConnector';
import QualityReport from './pages/QualityReport';
import MeetingDetail from './pages/MeetingDetail';
import ParticipantDetail from './pages/ParticipantDetail';
import MeetingAgent from './pages/MeetingAgent';
import MeetingMinutes from './pages/MeetingMinutes';
import TablePagination from './components/TablePagination';
import { useTablePagination } from './hooks/useTablePagination';
import './App.css';

// 直接在App.tsx中定义UserFeedback组件
const UserFeedback: React.FC = () => {
  const feedbacks = [
    {
      id: '1',
      userName: '张三',
      email: 'zhangsan@example.com',
      rating: 4,
      feedbackType: '功能建议',
      details: '希望能增加会议录制自动上传功能',
      comments: '目前需要手动上传，有点麻烦',
      submitTime: '2024-01-15 10:30'
    },
    {
      id: '2',
      userName: '李四',
      email: 'lisi@example.com',
      rating: 3,
      feedbackType: '性能问题',
      details: '会议中偶尔会出现画面卡顿',
      comments: '特别是在多人共享屏幕的时候',
      submitTime: '2024-01-14 15:20'
    }
  ];
  const {
    total,
    page,
    pageCount,
    pageSize,
    pagedRows,
    setPage,
    setPageSize
  } = useTablePagination(feedbacks);

  return (
    <div>
      <h2 className="page-title">用户反馈</h2>
      
      <div className="card">
        <h3>用户反馈列表</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>反馈人</th>
                <th>邮箱</th>
                <th>打分</th>
                <th>问题类型</th>
                <th>问题详情</th>
                <th>反馈意见</th>
                <th>提交时间</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(feedback => (
                <tr key={feedback.id}>
                  <td>{feedback.userName}</td>
                  <td>{feedback.email}</td>
                  <td>{'★'.repeat(feedback.rating)}{'☆'.repeat(5 - feedback.rating)}</td>
                  <td>{feedback.feedbackType}</td>
                  <td>{feedback.details}</td>
                  <td>{feedback.comments}</td>
                  <td>{feedback.submitTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination
          total={total}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
};

const AppLayout: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const content = document.querySelector('.main-content');
    if (content instanceof HTMLElement) {
      content.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <div className="app-container">
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            <span className="brand-icon" aria-hidden="true">
              <svg className="brand-logo-svg" viewBox="0 0 24 24">
                <rect x="2.5" y="6.5" width="14" height="11" rx="2" />
                <path d="M16.5 10.2h2.4l2.6-2.2v8l-2.6-2.2h-2.4z" />
                <circle cx="7.5" cy="12" r="1.2" />
                <circle cx="11.5" cy="12" r="1.2" />
                <path d="M7 3.5v2.2M12 3.5v2.2" />
              </svg>
            </span>
            <span>视频会议</span>
          </div>
          <div className="workspace-select">
            <span>全部</span>
            <span className="caret" aria-hidden="true" />
          </div>
        </div>
        <div className="topbar-right">
          <button type="button" className="icon-button" aria-label="通知">
            <span className="icon-dot" aria-hidden="true" />
          </button>
          <div className="user-chip">
            <span className="avatar">管</span>
            <span>管理员</span>
            <span className="caret" aria-hidden="true" />
          </div>
        </div>
      </header>
      <div className="app-body">
        <nav className="sidebar">
          <NavLink to="/" exact className="sidebar-item" activeClassName="active">
            <span className="sidebar-icon overview" aria-hidden="true">
              <svg className="sidebar-icon-svg" viewBox="0 0 16 16">
                <path d="M2.2 6.4L8 1.8l5.8 4.6v7.2H2.2z" />
              </svg>
            </span>
            概览
          </NavLink>
          <NavLink to="/meeting-agent" className="sidebar-item" activeClassName="active">
            <span className="sidebar-icon agent" aria-hidden="true">
              <svg className="sidebar-icon-svg" viewBox="0 0 16 16">
                <rect x="2.2" y="3.2" width="11.6" height="9.6" rx="1.6" />
                <circle cx="5.7" cy="8" r="1" />
                <circle cx="10.3" cy="8" r="1" />
                <path d="M6.2 10.6h3.6M8 1.8v1.4" />
              </svg>
            </span>
            会议智能体
          </NavLink>

          <div className="sidebar-group">
            <div className="sidebar-group-header">
              <span className="sidebar-icon meeting" aria-hidden="true">
                <svg className="sidebar-icon-svg" viewBox="0 0 16 16">
                  <rect x="2.2" y="2.2" width="11.6" height="11.6" rx="1.5" />
                  <path d="M2.2 6.5h11.6M6.5 2.2v11.6" />
                </svg>
              </span>
              <span className="sidebar-label">会议</span>
              <span className="sidebar-caret" aria-hidden="true" />
            </div>
            <NavLink to="/real-time" className="sidebar-subitem" activeClassName="active">
              实时会议
            </NavLink>
            <NavLink to="/historical" className="sidebar-subitem" activeClassName="active">
              历史会议
            </NavLink>
          </div>

          <div className="sidebar-group">
            <div className="sidebar-group-header">
              <span className="sidebar-icon minutes" aria-hidden="true">
                <svg className="sidebar-icon-svg" viewBox="0 0 16 16">
                  <path d="M4 2.2h8a1.4 1.4 0 0 1 1.4 1.4v8.8a1.4 1.4 0 0 1-1.4 1.4H4a1.4 1.4 0 0 1-1.4-1.4V3.6A1.4 1.4 0 0 1 4 2.2z" />
                  <path d="M5.2 5.4h5.6M5.2 8h5.6M5.2 10.6h3.2" />
                </svg>
              </span>
              <span className="sidebar-label">纪要中心</span>
              <span className="sidebar-caret" aria-hidden="true" />
            </div>
            <NavLink to="/meeting-minutes" className="sidebar-subitem" activeClassName="active">
              纪要列表
            </NavLink>
          </div>

          <div className="sidebar-group">
            <div className="sidebar-group-header">
              <span className="sidebar-icon cloud" aria-hidden="true">
                <svg className="sidebar-icon-svg" viewBox="0 0 16 16">
                  <path d="M5.2 12.8h6.3a2.3 2.3 0 0 0 .2-4.6A3.5 3.5 0 0 0 5.1 6a2.5 2.5 0 0 0 .1 5z" />
                </svg>
              </span>
              <span className="sidebar-label">云会议</span>
              <span className="sidebar-caret" aria-hidden="true" />
            </div>
            <NavLink to="/sip-devices" className="sidebar-subitem" activeClassName="active">
              SIP设备
            </NavLink>
          </div>

          <div className="sidebar-group">
            <div className="sidebar-group-header">
              <span className="sidebar-icon setting" aria-hidden="true">
                <svg className="sidebar-icon-svg" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="2.3" />
                  <path d="M8 1.7v1.6M8 12.7v1.6M1.7 8h1.6M12.7 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1" />
                </svg>
              </span>
              <span className="sidebar-label">设置</span>
              <span className="sidebar-caret" aria-hidden="true" />
            </div>
            <NavLink to="/participants" className="sidebar-subitem" activeClassName="active">
              用户管理
            </NavLink>
            <NavLink to="/quality-report" className="sidebar-subitem" activeClassName="active">
              质量报告
            </NavLink>
            <NavLink to="/feedback" className="sidebar-subitem" activeClassName="active">
              用户反馈
            </NavLink>
          </div>
        </nav>
        <main className="main-content">
          <div className="content-inner">
            <Switch>
              <Route exact path="/" component={Overview} />
              <Route exact path="/meeting-agent" component={MeetingAgent} />
              <Route exact path="/real-time" component={RealTimeMeetings} />
              <Route
                path="/real-time/:meetingId/participant/:participantId"
                component={ParticipantDetail}
              />
              <Route exact path="/real-time/:meetingId" component={MeetingDetail} />
              <Route exact path="/historical" component={HistoricalMeetings} />
              <Route
                path="/historical/:meetingId/participant/:participantId"
                component={ParticipantDetail}
              />
              <Route exact path="/historical/:meetingId" component={MeetingDetail} />
              <Route exact path="/meeting-minutes" component={MeetingMinutes} />
              <Route path="/cloud-meeting-connector" component={CloudMeetingConnector} />
              <Route path="/participants" component={ParticipantsList} />
              <Route path="/sip-devices" component={SipDevices} />
              <Route path="/feedback" component={UserFeedback} />
              <Route path="/quality-report" component={QualityReport} />
            </Switch>
          </div>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <Router>
    <AppLayout />
  </Router>
);

export default App;
